import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import ActiveArchivedToggle from '../components/ActiveArchivedToggle';
import { fetchSaIndividuals } from '../api/saApi';
import PageShell from '../../../components/layout/PageShell';
import PageHeader from '../components/PageHeader';
import DetailDrawer from '../components/DetailDrawer';
import FilterBar from '../components/FilterBar';
import ResourceDataTable from '../components/ResourceDataTable';
import StatusChip from '../components/StatusChip';
import { formatAdminDate, safeAdminError, shortenIdentifier } from '../components/adminPresentation';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const SuperAdminIndividualPage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') === 'archived' ? 'archived' : 'active';
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const loadUsers = useCallback(async () => {
    if (!token) {
      setUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetchSaIndividuals({ token, view });
      setUsers(response.users || []);
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load users right now.'));
    } finally {
      setIsLoading(false);
    }
  }, [token, view]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return (
    <PageShell containerClassName="p-0">
    <PageHeader title="Users" description="Review active and archived CrickArena user accounts." />
    <section className="sa-panel">
      <div className="sa-panel__body">
        <ActiveArchivedToggle />
        <FilterBar searchPlaceholder="Search users" searchValue={search} onSearchChange={setSearch} hasActiveFilters={Boolean(search)} onClear={() => setSearch('')} />
        <ResourceDataTable caption="CrickArena users" loading={isLoading} error={error} onRetry={loadUsers} items={users.filter((user) => `${user.name || ''} ${user.email || ''}`.toLowerCase().includes(search.toLowerCase()))} getRowLabel={(user) => user.name || user.email || 'User'} onInspect={setSelected} emptyTitle={search ? 'No matching users' : `No ${view} users`} emptyMessage={search ? 'Clear the search or try a different name or email.' : 'Users will appear here when available.'} columns={[
          { key: 'user', label: 'User', sortable: true, sortValue: (user) => user.name || user.email, render: (user) => <span className="sa-primary-cell"><strong>{user.name || 'Unnamed user'}</strong><small>{user.email || shortenIdentifier(user._id)}</small></span> },
          { key: 'role', label: 'Role', render: (user) => (user.roles || [user.role]).filter(Boolean).join(', ') || '—' },
          { key: 'affiliation', label: 'Affiliation', render: (user) => user.organization?.name || user.club?.name || user.community?.name || '—' },
          { key: 'status', label: 'Status', render: (user) => <StatusChip value={user.archived ? 'Archived' : user.enabled === false ? 'Disabled' : user.approvalStatus || 'Active'} /> },
          { key: 'active', label: 'Last active', sortable: true, sortValue: (user) => user.lastSeen || user.updatedAt, render: (user) => formatAdminDate(user.lastSeen || user.updatedAt, { withTime: true }) },
        ]} getActions={(user) => [{ label: 'View details', onSelect: () => setSelected(user) }, { label: 'Copy ID', onSelect: () => navigator.clipboard?.writeText(user._id || user.id) }]} />
      </div>
    </section>
    <DetailDrawer open={Boolean(selected)} title={selected?.name || selected?.email || 'User'} eyebrow="User overview" onClose={() => setSelected(null)} developerData={selected}><section><h3>Overview</h3><dl className="sa-detail-list"><div><dt>Status</dt><dd><StatusChip value={selected?.archived ? 'Archived' : selected?.enabled === false ? 'Disabled' : selected?.approvalStatus || 'Active'} /></dd></div><div><dt>Email</dt><dd>{selected?.email || '—'}</dd></div><div><dt>Role</dt><dd>{(selected?.roles || [selected?.role]).filter(Boolean).join(', ') || '—'}</dd></div><div><dt>Last active</dt><dd>{formatAdminDate(selected?.lastSeen || selected?.updatedAt, { withTime: true })}</dd></div><div><dt>Identifier</dt><dd>{shortenIdentifier(selected?._id || selected?.id)}</dd></div></dl></section></DetailDrawer>
  
    </PageShell>
  );
};

export default SuperAdminIndividualPage;
